// Vehicle trip ledger — LOCAL-ONLY append-only HASH-CHAIN denník knihy jázd.
// Autoritatívny dôkaz pred daňovým úradom; vehicle_trips je len UI projekcia.
// Spec: MasterProject/feature-ideas/ideas/todue-kniha-jazd-SPEC-v2.md §3 (review
// Codex SOL + Fable — „append-only nestačí, treba event ledger s hash predchodcu").
//
// LOCAL-ONLY (bloker B1, vzor decision_events): NIKDY v SYNC_STORES — sync ťahá
// pageSize=1000 bez pagination, append-only store by nad 1000 riadkov ticho
// strácal eventy. Sync ledgera príde ako samostatný segmentovaný task.
//
// Invarianty:
//   • seq = neprerušená rada per vozidlo (1-based), UNIQUE(vehicle_id, seq).
//   • prev_hash = entry_hash predošlého eventu (genesis pre prvý).
//   • entry_hash = sha256(prev_hash | canonical(semantic)) → tamper-evidence.
//   • put-if-absent na deterministické id (sha256("vtl-v1|" + eventKey)): rovnaký
//     eventKey + zhodný obsah = no-op (idempotentný retry); iný obsah = hard error.
//   • žiadny update, žiadny delete, žiadny prune (append-only).
//   • verifyLedgerChain deteguje dieru v seq alebo prepísaný/chýbajúci hash.
import { db } from './db';

// ─── Event taxonómia v1 — UZAVRETÝ enum (nový event = schema/test update) ──────
export const TRIP_LEDGER_EVENT_TYPES = [
  'trip_proposed',    // návrh z faktúry/šablóny/bloku (zdroj v payload.provenance)
  'trip_confirmed',   // user potvrdil (payload nesie diff návrh→potvrdené)
  'trip_amended',     // oprava pred uzávierkou obdobia
  'trip_voided',      // storno po uzávierke (payload.reason povinný)
  'trip_corrected',   // nový záznam nahrádzajúci stornovaný (payload.corrects_id)
  'odometer_recorded', // stav tachometra (odpočet: začiatok/koniec obdobia/ukončenie)
  'cost_recorded',    // náklad na vozidlo (PHM/servis/…)
  'cost_voided',      // storno nákladu po uzávierke (payload.reason povinný)
  'cost_corrected',   // opravný náklad nahrádzajúci stornovaný (payload.corrects_id)
  'period_closed',    // uzávierka obdobia (payload.manifest_hash)
  'period_reclosed',  // opätovná uzávierka po oprave
  'export_generated', // vygenerovaný kontrolný balík (payload.manifest_hash)
];

export const LEDGER_ACTOR_TYPES = ['user', 'system', 'engine'];

// Storno/oprava vyžadujú dôvod (dôkazná sila — viditeľná oprava dôveru zvyšuje).
const REASON_REQUIRED_EVENTS = new Set(['trip_voided', 'trip_corrected', 'cost_voided', 'cost_corrected']);

// ─── SHA-256 hex via Web Crypto (vzor decisionEvents.sha256Hex) ───────────────
// Exportované — kontrolný balík (vehicleExport) hashuje rovnakým algoritmom ako ledger.
export async function sha256Hex(text) {
  const subtle = globalThis.crypto && globalThis.crypto.subtle;
  if (!subtle) throw new Error('vehicleLedger: Web Crypto (crypto.subtle) not available');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i += 1) hex += view[i].toString(16).padStart(2, '0');
  return hex;
}

// ─── Canonical JSON — rekurzívne zotriedené kľúče (deterministický hash) ───────
// STRICT: undefined a NaN/Infinity sú zakázané (JSON.stringify ich mení na "null"
// → hash kolízia NaN==null a undefined sa v DB serializácii vynechá, takže čerstvo
// zapísaný event by neprešiel vlastným verifierom). Radšej odmietnuť vstup (Codex #6).
export function canonicalize(value) {
  if (value === null) return 'null';
  if (value === undefined) {
    throw new Error('[v2/vehicleLedger] canonicalize: undefined nie je povolené (použi null)');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new Error('[v2/vehicleLedger] canonicalize: NaN/Infinity nie je povolené v hashovanom obsahu');
  }
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`;
  if (typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalize(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

// ─── device_id_hash — stabilný hash inštalácie (zdieľaný LS key s decisionEvents) ─
const DEVICE_ID_LS_KEY = 'todue_v2_device_id_hash';
let _deviceIdHash = null;
async function getDeviceIdHash() {
  if (_deviceIdHash) return _deviceIdHash;
  let cached = null;
  try {
    cached = globalThis.localStorage ? globalThis.localStorage.getItem(DEVICE_ID_LS_KEY) : null;
  } catch { /* localStorage nedostupné */ }
  if (cached) { _deviceIdHash = cached; return cached; }
  const hash = await sha256Hex(`device-v1|${crypto.randomUUID()}`);
  try {
    if (globalThis.localStorage) globalThis.localStorage.setItem(DEVICE_ID_LS_KEY, hash);
  } catch { /* best-effort */ }
  _deviceIdHash = hash;
  return hash;
}

// occurredAt validácia: ISO tvar + reálny kalendárny dátum date časti + parsovateľné.
function isValidIsoInstant(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})([T ][\d:.]+([+-]\d{2}:?\d{2}|Z)?)?$/.exec(s);
  if (!m) return false;
  const [, y, mo, d] = m;
  const yy = Number(y); const mm = Number(mo); const dd = Number(d);
  const dt = new Date(Date.UTC(yy, mm - 1, dd));
  if (dt.getUTCFullYear() !== yy || dt.getUTCMonth() !== mm - 1 || dt.getUTCDate() !== dd) return false;
  return !Number.isNaN(Date.parse(s));
}

// ─── Genesis prev_hash per vozidlo — kotva reťaze ─────────────────────────────
export async function genesisHash(vehicleId) {
  return sha256Hex(`vehicle-trip-ledger-genesis-v1|${vehicleId}`);
}

// Deterministické ledger event id z eventKey (= PK v vehicle_trip_ledger). Umožňuje
// callerom idempotency-safe guardy: „patrí tento projekčný riadok tomuto eventKey?".
export async function tripLedgerEventId(eventKey) {
  return sha256Hex(`vtl-v1|${eventKey}`);
}

// Obsah, ktorý vstupuje do entry_hash — VIAŽE identitu (id z eventKey), pôvod
// (device_id_hash), čas vzniku (created_at) aj vecný obsah (Codex #4: bez toho
// sa dá id/created_at/device_id_hash prepísať bez detekcie — napr. spätne
// predstierať že záznam vznikol v deň jazdy). Idempotentný retry nevytvára nový
// hash — put-if-absent vráti existujúci riadok, takže created_at/device sa
// nemenia po prvom zápise.
function hashedContent(row) {
  return {
    id: row.id,
    vehicle_id: row.vehicle_id,
    seq: row.seq,
    event_type: row.event_type,
    trip_id: row.trip_id ?? null,
    payload: row.payload ?? null,
    actor_type: row.actor_type,
    actor_id_hash: row.actor_id_hash ?? null,
    device_id_hash: row.device_id_hash,
    occurred_at: row.occurred_at,
    created_at: row.created_at,
  };
}

// Put-if-absent identita — biz obsah + occurred_at (deklarovaný čas jazdy JE
// vecný obsah, Codex #3: replay rovnaký eventKey s iným occurred_at NESMIE byť
// ticho no-op). BEZ created_at/device_id_hash/seq (tie určuje prvý zápis a medzi
// retry pokusmi sa legitímne líšia).
function stableIdentity(row) {
  return canonicalize({
    vehicle_id: row.vehicle_id,
    event_type: row.event_type,
    trip_id: row.trip_id ?? null,
    payload: row.payload ?? null,
    actor_type: row.actor_type,
    actor_id_hash: row.actor_id_hash ?? null,
    occurred_at: row.occurred_at ?? null,
  });
}

async function computeEntryHash(prevHash, row) {
  return sha256Hex(`${prevHash}|${canonicalize(hashedContent(row))}`);
}

// actor: string ('user') alebo { type, id_hash }.
function normalizeActor(actor) {
  if (typeof actor === 'string') return { type: actor, id_hash: null };
  if (actor && typeof actor === 'object') return { type: actor.type, id_hash: actor.id_hash ?? null };
  return { type: null, id_hash: null };
}

// Posledný (najvyšší seq) event pre vozidlo — kotva pre ďalší prev_hash.
// `conn` = db (default, vlastná tx) alebo tx handle z db.transaction('rw', …) —
// aby append vedel bežať ATOMICKY spolu s projekciou vehicle_trips (vehicleTrips.js).
async function lastEvent(vehicleId, conn = db) {
  const rows = await conn.vehicle_trip_ledger.toArray();
  let best = null;
  for (const r of rows) {
    if (r.vehicle_id !== vehicleId) continue;
    if (!best || r.seq > best.seq) best = r;
  }
  return best;
}

/**
 * appendTripEvent — jediná zápisová cesta do vehicle_trip_ledger.
 * Put-if-absent na deterministické id (z eventKey): idempotentný retry = no-op,
 * iný obsah pod tým istým eventKey = hard error. Prideľuje ďalší seq, reťazí
 * entry_hash na prev_hash. Race na seq (UNIQUE) → 1× retry s prepočítaným seq.
 *
 * @param {string} eventKey  deterministický kľúč operácie (napr. `${tripId}|confirm`),
 *   crypto.randomUUID() pre skutočne jednorazovú operáciu.
 * @param {object} [conn]  db (default) alebo tx handle z db.transaction('rw', …).
 *   Keď je podaný tx handle, append beží VNÚTRI tej transakcie → ledger event aj
 *   projekcia vehicle_trips commitnú spolu alebo vôbec (vehicleTrips.recordTripEvent).
 *   V tx je store exkluzívne zamknutý → seq kolízia s iným eventKey nenastane;
 *   retry loop tak reálne rieši len idempotentný duplicitný id.
 */
export async function appendTripEvent({
  eventKey,
  vehicleId,
  eventType,
  tripId,
  payload,
  actor,
  occurredAt,
  conn = db,
} = {}) {
  if (!TRIP_LEDGER_EVENT_TYPES.includes(eventType)) {
    throw new Error(`[v2/vehicleLedger] neznámy event_type: ${JSON.stringify(eventType)} — enum je uzavretý`);
  }
  if (!vehicleId || typeof vehicleId !== 'string') {
    throw new Error(`[v2/vehicleLedger] vehicleId je povinný, dostal ${JSON.stringify(vehicleId)}`);
  }
  if (!eventKey || typeof eventKey !== 'string') {
    throw new Error(`[v2/vehicleLedger] eventKey je povinný (deterministický kľúč operácie), dostal ${JSON.stringify(eventKey)}`);
  }
  // occurredAt POVINNÝ a STABILNÝ naprieč retry. occurred_at je súčasť stableIdentity
  // (put-if-absent) — chráni pred tichým no-op pri kolízii eventKey s iným časom (Codex #3).
  // Keby default padal na Date.now(), idempotentný retry o sekundy neskôr (po sieťovom
  // zlyhaní) by mal iný occurred_at → reconcile by hodil „iný obsah" namiesto no-op =
  // strata operácie. Preto žiadny now-fallback: caller MUSÍ podať stabilný čas
  // (deklarovaný čas jazdy, alebo čas operácie z UI eventu — NIE Date.now() pri retry).
  // ISO tvar + reálny dátum (Codex #5: samotný „neprázdny string" prepustí junk ako
  // 'not-an-ISO-time'; a Date.parse normalizuje pretečenie 31.2.→3.3., takže nemožný
  // kalendárny dátum by prešiel — over komponenty date časti spätným čítaním).
  if (!occurredAt || typeof occurredAt !== 'string' || !isValidIsoInstant(occurredAt)) {
    throw new Error(`[v2/vehicleLedger] occurredAt musí byť platný ISO čas (stabilný, nie Date.now() — inak retry rozbije idempotenciu), dostal ${JSON.stringify(occurredAt)}`);
  }
  const a = normalizeActor(actor);
  if (!LEDGER_ACTOR_TYPES.includes(a.type)) {
    throw new Error(`[v2/vehicleLedger] actor_type musí byť ${LEDGER_ACTOR_TYPES.join('|')}, dostal ${JSON.stringify(a.type)}`);
  }
  if (REASON_REQUIRED_EVENTS.has(eventType)) {
    const reason = payload && typeof payload === 'object' ? payload.reason : null;
    if (!reason || typeof reason !== 'string' || !reason.trim()) {
      throw new Error(`[v2/vehicleLedger] ${eventType} vyžaduje neprázdny payload.reason (viditeľná oprava = dôkazná sila)`);
    }
  }

  const id = await sha256Hex(`vtl-v1|${eventKey}`);
  const now = new Date().toISOString(); // len created_at (NIE v stableIdentity — smie sa medzi retry líšiť)
  const occurred = occurredAt; // stabilný, povinný (viď guard vyššie)
  const deviceIdHash = await getDeviceIdHash();

  const incoming = {
    id, vehicle_id: vehicleId, event_type: eventType, trip_id: tripId ?? null,
    payload: payload ?? null, actor_type: a.type, actor_id_hash: a.id_hash, occurred_at: occurred,
  };

  // Put-if-absent: existujúci id → porovnaj stabilnú identitu, no-op alebo hard error.
  const existing = await conn.vehicle_trip_ledger.get(id);
  if (existing) return reconcileExisting(existing, incoming);

  // Prideľ ďalší seq + reťaz hash. Race backstop: UNIQUE(vehicle_id, seq) → retry.
  // MAX_APPEND_ATTEMPTS > 2, aby aj 3+ súbežných zápisov konvergovalo (Codex #5).
  const MAX_APPEND_ATTEMPTS = 12;
  for (let attempt = 0; attempt < MAX_APPEND_ATTEMPTS; attempt += 1) {
    const prev = await lastEvent(vehicleId, conn);
    const seq = prev ? prev.seq + 1 : 1;
    const prevHash = prev ? prev.entry_hash : await genesisHash(vehicleId);
    const row = {
      id,
      vehicle_id: vehicleId,
      seq,
      event_type: eventType,
      trip_id: tripId ?? null,
      prev_hash: prevHash,
      entry_hash: '', // dopočíta sa nižšie
      payload: payload ?? null,
      actor_type: a.type,
      actor_id_hash: a.id_hash,
      device_id_hash: deviceIdHash,
      occurred_at: occurred,
      created_at: now,
    };
    row.entry_hash = await computeEntryHash(prevHash, row);
    try {
      await conn.vehicle_trip_ledger.add(row);
      return row;
    } catch (e) {
      // Duplicitný id (concurrent rovnaký eventKey) → reconcile put-if-absent.
      const again = await conn.vehicle_trip_ledger.get(id);
      if (again) return reconcileExisting(again, incoming);
      // Inak duplicitný seq (concurrent iný eventKey) → prepočítaj seq a skús znova.
      if (attempt === MAX_APPEND_ATTEMPTS - 1) throw e;
    }
  }
  throw new Error('[v2/vehicleLedger] append zlyhal po retry (seq kolízia)');
}

// Existujúci riadok pod tým istým id: zhodná stabilná identita (vrátane
// occurred_at) = no-op, inak hard error. Rieši replay útok (Codex #3).
function reconcileExisting(existing, incoming) {
  if (stableIdentity(existing) === stableIdentity(incoming)) return existing; // idempotentný retry
  throw new Error(`[v2/vehicleLedger] id ${incoming.id} už existuje s INÝM obsahom — append odmietnutý (put-if-absent)`);
}

// listTripLedger(vehicleId?, conn?) — eventy zotriedené podľa seq (celý denník ak bez id).
// conn = tx handle pre čítanie vnútri transakcie (rebuildProjection vo vehicleTrips.js).
export async function listTripLedger(vehicleId, conn = db) {
  const rows = await conn.vehicle_trip_ledger.toArray();
  const filtered = vehicleId ? rows.filter((r) => r.vehicle_id === vehicleId) : rows;
  return filtered.sort((x, y) => {
    if (x.vehicle_id !== y.vehicle_id) return x.vehicle_id < y.vehicle_id ? -1 : 1;
    return x.seq - y.seq;
  });
}

/**
 * verifyLedgerChain(vehicleId, { expectedCount, expectedHeadHash }) — over integritu.
 * Deteguje: dieru/duplicitu v seq, nesprávny prev_hash (prepis/zmazanie prostredného),
 * prepočítaný entry_hash (tampering s obsahom).
 *
 * ⚠️ LIMIT (Codex #4): samotná reťaz NEZACHYTÍ truncation CHVOSTA (zmazanie
 * posledného eventu) ani prepočítanie celej reťaze útočníkom s prístupom ku kódu —
 * to je fundamentálny limit hash-chainu s kľúčom v tom istom klientovi. Preto:
 *   • `expectedCount` — ak caller pozná počet eventov z NEZÁVISLÉHO zdroja
 *     (napr. z period_closed manifestu alebo zo servera), truncation sa odhalí.
 *   • `expectedHeadHash` — očakávaný entry_hash posledného eventu z nezávislého zdroja.
 * Plná ochrana proti truncation+rechain = externé časové ukotvenie (transparency
 * log / server timestamp) — samostatný balík (SPEC-v2 §3 „miesto pre externé ukotvenie").
 */
export async function verifyLedgerChain(vehicleId, { expectedCount, expectedHeadHash } = {}) {
  const chain = await listTripLedger(vehicleId);
  const errors = [];

  if (typeof expectedCount === 'number' && chain.length !== expectedCount) {
    errors.push(`počet eventov nesedí: očakávané ${expectedCount}, nájdené ${chain.length} (možná truncation chvosta)`);
  }
  if (chain.length === 0) {
    return { ok: errors.length === 0, errors, length: 0 };
  }

  let expectedPrev = await genesisHash(vehicleId);
  for (let i = 0; i < chain.length; i += 1) {
    const row = chain[i];
    const expectedSeq = i + 1;
    if (row.seq !== expectedSeq) {
      errors.push(`seq medzera/duplicita: očakávané ${expectedSeq}, nájdené ${row.seq} (id ${row.id})`);
    }
    if (row.prev_hash !== expectedPrev) {
      errors.push(`prev_hash nesedí na seq ${row.seq}: reťaz prerušená (prepis alebo chýbajúci event)`);
    }
    const recomputed = await computeEntryHash(row.prev_hash, row);
    if (recomputed !== row.entry_hash) {
      errors.push(`entry_hash nesedí na seq ${row.seq}: obsah eventu bol zmenený (id ${row.id})`);
    }
    expectedPrev = row.entry_hash;
  }

  if (expectedHeadHash && chain[chain.length - 1].entry_hash !== expectedHeadHash) {
    errors.push('head hash nesedí na očakávaný (truncation chvosta alebo prepočítaná reťaz)');
  }
  return { ok: errors.length === 0, errors, length: chain.length };
}
